import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Grid2 as Grid,
  Card,
  IconButton,
  TextField,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import validator from "validator";
import axios from "axios";
import { handlePhoneInputChange as formatPhone } from "../../functions/permitFormHelpers";
import { floridaCounties } from "../../data/counties";

const parseCommissionExpiry = (expiryString) => {
  if (!expiryString || typeof expiryString !== "string") return null;
  if (!expiryString.includes("/")) return null;

  const [month, day, year] = expiryString.split("/");
  return dayjs(`${year}-${month}-${day}`);
};

const isCommissionExpired = (expiryDate) => {
  if (!expiryDate) return false;
  if (dayjs.isDayjs(expiryDate)) {
    return expiryDate.isBefore(dayjs(), "day");
  }
  return false;
};

const getNotaryTypeLabel = (notaryType) => {
  switch (notaryType) {
    case 0:
      return "IPEN Only";
    case 1:
      return "IPEN & RON";
    case 2:
      return "IPEN & RON";
    default:
      return "Unknown";
  }
};

const EnterpriseAddNotaryModal = ({
  open,
  onClose,
  currentSession,
  draftValues,
  foundNotary,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    state: "",
    county: "",
    email: "",
    phone: "",
    commissionExpiry: "",
  });
  const [notaryState, setNotaryState] = useState("");
  const [notaryCounty, setNotaryCounty] = useState("");
  const [notaryEmail, setNotaryEmail] = useState("");
  const [notaryPhone, setNotaryPhone] = useState("");
  const [commissionExpiry, setCommissionExpiry] = useState(null);

  useEffect(() => {
    if (!open) return;

    setLoading(false);
    setErrorText("");
    setFieldErrors({
      state: "",
      county: "",
      email: "",
      phone: "",
      commissionExpiry: "",
    });

    setNotaryState(foundNotary?.state || "Florida");
    setNotaryCounty(foundNotary?.county || "");
    setNotaryEmail(draftValues?.email || foundNotary?.email || "");
    setNotaryPhone(foundNotary?.phone || "");
    setCommissionExpiry(parseCommissionExpiry(foundNotary?.commissionExpiry));
  }, [open, foundNotary, draftValues]);

  const helperText = useMemo(
    () =>
      "Email must be unique across notaries, users, and subusers collections.",
    [],
  );

  const validateFields = () => {
    const nextErrors = {
      state: "",
      county: "",
      email: "",
      phone: "",
      commissionExpiry: "",
    };
    let isValid = true;

    if (!notaryState?.trim()) {
      nextErrors.state = "State is required";
      isValid = false;
    }

    if (!notaryCounty?.trim()) {
      nextErrors.county = "County is required";
      isValid = false;
    }

    if (!notaryEmail?.trim()) {
      nextErrors.email = "Email is required";
      isValid = false;
    } else if (!validator.isEmail(notaryEmail.trim())) {
      nextErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    if (!notaryPhone?.trim()) {
      nextErrors.phone = "Phone number is required";
      isValid = false;
    } else if (
      !validator.isMobilePhone(notaryPhone, "any", { strictMode: false })
    ) {
      nextErrors.phone = "Please enter a valid phone number";
      isValid = false;
    }

    if (!commissionExpiry) {
      nextErrors.commissionExpiry = "Commission expiry date is required";
      isValid = false;
    } else if (isCommissionExpired(commissionExpiry)) {
      nextErrors.commissionExpiry = "Commission expiry must be in the future";
      isValid = false;
    }

    setFieldErrors(nextErrors);
    return isValid;
  };

  const handleInvite = async () => {
    if (!validateFields()) {
      setErrorText("Please fix the errors in the form before proceeding.");
      return;
    }

    setLoading(true);
    setErrorText("");

    try {
      const values = {
        ...draftValues,
        email: notaryEmail.trim().toLowerCase(),
        commission_number:
          foundNotary?.commissionNumber || draftValues?.commission_number,
        commission_expiry: commissionExpiry.format("MM/DD/YYYY"),
        state: notaryState,
        county: notaryCounty,
        phone: notaryPhone,
      };

      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/enterprise/notaries/create`,
        { values, currentSession },
        { withCredentials: true },
      );

      onSuccess?.(resp?.data);
      onClose?.();
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to invite notary.";

      if (message.toLowerCase().includes("email")) {
        setFieldErrors((prev) => ({ ...prev, email: message }));
      } else {
        setErrorText(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={loading ? undefined : onClose}
      sx={{ overflow: "auto" }}
    >
      <Box
        sx={{
          position: "relative",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(920px, 92vw)",
        }}
      >
        <Card
          sx={{
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: 3,
          }}
        >
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              bgcolor: "background.paper",
              borderBottom: "1px solid #e0e0e0",
              px: 2,
              pt: 2,
              pb: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="h5" color="primary">
                Add Enterprise Notary
              </Typography>
              <IconButton onClick={onClose} disabled={loading}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Typography variant="body1" gutterBottom>
              We found a notary record in the registry. Review the details below
              and send the invitation to your enterprise notary pool.
            </Typography>
          </Box>

          <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    p: 2,
                    border: `2px solid ${foundNotary?.is_expired ? "#f44336" : "#4caf50"}`,
                    borderRadius: 2,
                    bgcolor: foundNotary?.is_expired ? "#ffebee" : "#e8f5e9",
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    {foundNotary?.is_expired
                      ? "⚠️ Notary Found (Commission Expired)"
                      : "✓ Notary Found"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Name:</strong> {draftValues?.first}{" "}
                    {draftValues?.last}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Invited Email:</strong> {draftValues?.email}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Commission Number:</strong>{" "}
                    {foundNotary?.commissionNumber}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Notary Type:</strong>{" "}
                    {getNotaryTypeLabel(foundNotary?.notaryType)}
                  </Typography>
                  {!!foundNotary?.address && (
                    <Typography variant="body2">
                      <strong>Address:</strong> {foundNotary.address}
                    </Typography>
                  )}
                  {!!foundNotary?.city && (
                    <Typography variant="body2">
                      <strong>City:</strong> {foundNotary.city}
                    </Typography>
                  )}
                  {!!foundNotary?.zip && (
                    <Typography variant="body2">
                      <strong>Zip:</strong> {foundNotary.zip}
                    </Typography>
                  )}
                </Box>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Grid
                  container
                  spacing={2}
                  sx={{
                    border: "1px solid #d3d3d3",
                    borderRadius: 2,
                    p: 2,
                    bgcolor: "background.paper",
                  }}
                >
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="h6" align="center" color="primary">
                      Required Fields
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      select
                      label="State"
                      value={notaryState}
                      onChange={(e) => {
                        setNotaryState(e.target.value);
                        setFieldErrors((prev) => ({ ...prev, state: "" }));
                      }}
                      fullWidth
                      error={!!fieldErrors.state}
                      helperText={fieldErrors.state}
                    >
                      <MenuItem value="Florida">Florida</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      select
                      label="County"
                      value={notaryCounty}
                      onChange={(e) => {
                        setNotaryCounty(e.target.value);
                        setFieldErrors((prev) => ({ ...prev, county: "" }));
                      }}
                      fullWidth
                      error={!!fieldErrors.county}
                      helperText={fieldErrors.county}
                    >
                      {floridaCounties.map((county) => (
                        <MenuItem key={county} value={county}>
                          {county}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      label="Phone No."
                      value={notaryPhone}
                      onChange={(e) => {
                        setNotaryPhone(formatPhone(e));
                        setFieldErrors((prev) => ({ ...prev, phone: "" }));
                      }}
                      fullWidth
                      error={!!fieldErrors.phone}
                      helperText={fieldErrors.phone}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Email"
                      type="email"
                      value={notaryEmail}
                      onChange={(e) => {
                        setNotaryEmail(e.target.value);
                        setFieldErrors((prev) => ({ ...prev, email: "" }));
                      }}
                      fullWidth
                      error={!!fieldErrors.email}
                      helperText={fieldErrors.email || helperText}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Commission Expiry Date"
                        value={commissionExpiry}
                        onChange={(date) => {
                          setCommissionExpiry(date);
                          setFieldErrors((prev) => ({
                            ...prev,
                            commissionExpiry: "",
                          }));
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!fieldErrors.commissionExpiry,
                            helperText: fieldErrors.commissionExpiry,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              </Grid>

              {!!errorText && (
                <Grid size={{ xs: 12 }}>
                  <Typography color="error" textAlign="center">
                    {errorText}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>

          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              zIndex: 10,
              bgcolor: "background.paper",
              borderTop: "1px solid #e0e0e0",
              px: 2,
              py: 2,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button variant="outlined" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              onClick={handleInvite}
              loading={loading}
              loadingPosition="start"
              startIcon={loading ? <CircularProgress size={18} /> : null}
            >
              Invite Notary
            </LoadingButton>
          </Box>
        </Card>
      </Box>
    </Modal>
  );
};

export default EnterpriseAddNotaryModal;
